import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import cn from 'classnames';
import PropTypes from 'prop-types';
import ReactModal from 'react-modal';
import { applyClasses, applyProps } from '../shared/js';
import { Button, Icons, Typography, HTML } from '..';

import styles from './modal.module.scss';

const Modal = ({
  id,
  title,
  isOpen,
  onRequestClose,
  noUnderline,
  titleLeft,
  titleCenter,
  titleRight,
  titleSize,
  children,
  shouldCloseOnOverlayClick,
  titleClassName,
  hideMobileCloseLabel,
  className,
  ...props
}) => {
  useEffect(() => {
    if (isOpen) document.body.classList.add(styles.scrollLockMobile);
    else document.body.classList.remove(styles.scrollLockMobile);

    return () => {
      document.body.classList.remove(styles.scrollLockMobile);
    };
  }, [isOpen]);

  return (
    <ReactModal
      id={id}
      className={cn(
        styles.modal,
        // eslint-disable-next-line react/prop-types
        className,
        applyClasses(props, styles),
        {
          [styles.open]: isOpen,
        }
      )}
      overlayClassName={styles.overlay}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      appElement={document.getElementById('root')}
      closeTimeoutMS={300}
      shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
      {...props}
      {...applyProps(props)}
    >
      <HTML hideOnDesktop className={styles.closeSection}>
        <Button className={styles.closeButton} onClick={onRequestClose}>
          {!hideMobileCloseLabel && (
            <span className={styles.closeLabel}>
              <FormattedMessage id="buttons.close" defaultMessage="Close" />
            </span>
          )}
          <Icons.NavClose />
        </Button>
      </HTML>
      <div
        className={cn(styles.top, {
          [styles.hasTitle]: title,
          [styles.noUnderline]: noUnderline,
        })}
      >
        {title && (
          <Typography
            h1
            center={titleCenter && !titleLeft && !titleRight}
            left={titleLeft}
            right={titleRight}
            bold
            sizeOverride={titleSize}
            className={cn(styles.title, titleClassName)}
          >
            {title}
          </Typography>
        )}
        <Button
          desktopOnly
          className={styles.closeButton}
          onClick={onRequestClose}
        >
          <span className={styles.closeLabel}>
            <FormattedMessage id="buttons.close" defaultMessage="Close" />
          </span>
          <Icons.NavClose />
        </Button>
      </div>
      {children}
    </ReactModal>
  );
};

Modal.propTypes = {
  /** The ID of the modal. Useful to select the modal during testing. */
  id: PropTypes.string,
  /** The title shown in the modal */
  title: PropTypes.string,
  /** Whether or not the modal is open */
  isOpen: PropTypes.bool,
  /** Callback function called when the modal is closed */
  onRequestClose: PropTypes.func,
  /** Render a narrow modal */
  narrow: PropTypes.bool,
  /** Remove the underline under the title */
  noUnderline: PropTypes.bool,
  /** Left-align the title */
  titleLeft: PropTypes.bool,
  /** Center-align the title */
  titleCenter: PropTypes.bool,
  /** Right-align the title */
  titleRight: PropTypes.bool,
  /** Use the responsive sizes for the title */
  responsive: PropTypes.bool,
  /** Override the size of the title */
  titleSize: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /** The contents of the modal */
  children: PropTypes.any,
  /** Close the modal when clicking on the overlay background */
  shouldCloseOnOverlayClick: PropTypes.bool,
  /** Custom classname for the title element */
  titleClassName: PropTypes.string,
  /** Hide the close label on the mobile modal */
  hideMobileCloseLabel: PropTypes.bool,
  /** Custom class name for the modal */
  className: PropTypes.string,
};

Modal.defaultProps = {
  title: undefined,
  isOpen: false,
  onRequestClose: undefined,
  narrow: undefined,
  noUnderline: false,
  titleLeft: false,
  titleCenter: true,
  titleRight: false,
  responsive: false,
  titleSize: undefined,
  children: undefined,
  shouldCloseOnOverlayClick: true,
};

export default Modal;
